import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchTemplateTypes, TemplateType } from '@/services/templateTypes';
import { toast } from 'sonner';

interface ContentTypeSelectorProps {
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
  className?: string;
}

const ContentTypeSelector: React.FC<ContentTypeSelectorProps> = ({
  selectedTypes,
  onTypesChange,
  className
}) => {
  const [templateTypes, setTemplateTypes] = useState<TemplateType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTemplateTypes = async () => {
      try {
        const types = await fetchTemplateTypes();
        setTemplateTypes(types);
        // Select all types by default
        onTypesChange(types.map(type => type.id));
      } catch (error) {
        console.error('Error loading template types:', error);
        toast.error('Failed to load content types');
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplateTypes();
  }, [onTypesChange]);

  const toggleType = (typeId: string) => {
    if (selectedTypes.includes(typeId)) {
      onTypesChange(selectedTypes.filter(id => id !== typeId));
    } else {
      onTypesChange([...selectedTypes, typeId]);
    }
  };

  const toggleAll = () => {
    if (selectedTypes.length === templateTypes.length) {
      onTypesChange([]);
    } else {
      onTypesChange(templateTypes.map(type => type.id));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn(
            "justify-between",
            selectedTypes.length === 0 && "text-muted-foreground",
            className
          )}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : selectedTypes.length > 0 ? (
            `${selectedTypes.length} type${selectedTypes.length === 1 ? '' : 's'} selected`
          ) : (
            "Select content types"
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="flex flex-col">
          <Button
            variant="ghost"
            className="justify-start px-2 py-1.5 h-auto border-b"
            onClick={toggleAll}
          >
            <Check
              className={cn(
                "mr-2 h-4 w-4",
                selectedTypes.length === templateTypes.length ? "opacity-100" : "opacity-0"
              )}
            />
            Select All
          </Button>
          {templateTypes.map((type) => (
            <Button
              key={type.id}
              variant="ghost"
              className={cn(
                "justify-start px-2 py-1.5 h-auto",
                selectedTypes.includes(type.id) && "bg-accent"
              )}
              onClick={() => toggleType(type.id)}
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  selectedTypes.includes(type.id) ? "opacity-100" : "opacity-0"
                )}
              />
              {type.name}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ContentTypeSelector; 